import {
    createContext,
    useState,
    useEffect,
    useContext,
    type ReactNode,
} from "react";
import { registerSW } from "virtual:pwa-register";
import PWAUpdateBanner from "@/components/PWAUpdateBanner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// TypeScript interface for beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface PWAContextValue {
    needsRefresh: boolean;
    canInstall: boolean;
    isInstalled: boolean;
    isInstalling: boolean;
    install: () => Promise<void>;
}

const PWAContext = createContext<PWAContextValue>({
    needsRefresh: false,
    canInstall: false,
    isInstalled: false,
    isInstalling: false,
    install: async () => {},
});

export const PWAProvider = ({ children }: { children: ReactNode }) => {
    const [needsRefresh, setNeedsRefresh] = useState(false);
    const [deferredPrompt, setDeferredPrompt] =
        useState<BeforeInstallPromptEvent | null>(null);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isInstalling, setIsInstalling] = useState(false);

    useEffect(() => {
        // Register service worker with error handling
        try {
            registerSW({
                onNeedRefresh() {
                    setNeedsRefresh(true);
                },
                onOfflineReady() {
                    console.log("Ready for offline use");
                },
                onRegisterError(error) {
                    console.error("Service Worker registration failed:", error);
                },
            });
        } catch (error) {
            console.error("Failed to register service worker:", error);
        }

        // Properly typed event handler
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };

        // Properly typed event handler
        const handleAppInstalled = () => {
            setIsInstalled(true);
            setDeferredPrompt(null);
        };

        // Check if app is already installed
        const checkInstalled = () => {
            const isStandalone = window.matchMedia(
                "(display-mode: standalone)",
            ).matches;
            const isInWebAppiOS =
                "standalone" in window.navigator &&
                (window.navigator as Navigator & { standalone?: boolean })
                    .standalone === true;
            setIsInstalled(isStandalone || isInWebAppiOS);
        };

        checkInstalled();
        
        // Add event listeners
        window.addEventListener(
            "beforeinstallprompt",
            handleBeforeInstallPrompt,
        );
        window.addEventListener("appinstalled", handleAppInstalled);

        // Cleanup: remove event listeners with the SAME function references
        return () => {
            window.removeEventListener(
                "beforeinstallprompt",
                handleBeforeInstallPrompt,
            );
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;

        setIsInstalling(true);

        try {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            
            console.log(`Install prompt outcome: ${outcome}`);
            
            if (outcome === "accepted") {
                console.log("User accepted the install prompt");
            } else {
                console.log("User dismissed the install prompt");
            }
            
            setDeferredPrompt(null);
        } catch (error) {
            console.error("Error during installation:", error);
        } finally {
            setIsInstalling(false);
        }
    };

    return (
        <PWAContext.Provider
            value={{
                needsRefresh,
                canInstall: !!deferredPrompt && !isInstalled,
                isInstalled,
                isInstalling,
                install: handleInstall,
            }}
        >
            {children}
            {needsRefresh && (
                <PWAUpdateBanner
                    onDismiss={() => setNeedsRefresh(false)}
                    onReload={() => window.location.reload()}
                />
            )}
            {deferredPrompt && (
                <PWAInstallPrompt
                    onInstall={handleInstall}
                    onDismiss={() => setDeferredPrompt(null)}
                    isInstalling={isInstalling}
                />
            )}
        </PWAContext.Provider>
    );
};

export const usePWA = () => useContext(PWAContext);
